import * as React from 'react';
import { View, ScrollView, Pressable, Alert, Platform } from 'react-native';
import { Text, Icon, Button } from 'react-native-elements';
import moment from 'moment';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import useNotProcessedOrders from 'hooks/useNotProcessedOrders';
import { styles } from 'styles/screens/ordersScreen';
import {
  deliverOrderByQr,
  getInProcessOrders,
  getPaginatedOrders,
} from 'services/ordersService';
import OrdersList from 'components/OrdersList';
import InputSearch from 'components/InputSearch';
import OrderDeliveryActionMenu from 'components/OrderDeliveryActionMenu';
import OrderStatusActionMenu from 'components/OrderStatusActionMenu';
import AppContext from 'contexts/AppContext';
import OrderContext from 'contexts/OrderContext';
import useMediumScreen from 'hooks/useMediumScreen';
import useChangeAuthData from 'hooks/useChangeAuthData';
import useOrientationPortrait from 'hooks/useOrientationPortrait';
import Order from 'types/models/Order';
import Commerce from 'types/models/Commerce';
import OrdersScreenProps from 'types/screens/OrdersScreenProps';
import UserRoleType from 'types/enums/UserRoleType';
import OrderStatusType from 'types/enums/OrderStatusType';
import QRScanner from 'components/QrScanner';
import { TouchableOpacity } from 'react-native-gesture-handler';
import SuccessModal from 'components/SuccessModal';
import useResponsiveContainer from 'hooks/useResponsiveContainer';
import parseAmounts from 'utils/parseAmounts';
import { SupportedLanguages } from 'types/enums/LanguageType';
import { handleTicketsImpression, handleUniqueProductImpression } from 'utils/orders';
import { getPaymentKey } from 'utils/payment';

interface InProcessOrders {
  pending: number;
  inProcess: number;
  toDelivery: number;
}

export default ({ navigation }: OrdersScreenProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { processPendingOrders } = useNotProcessedOrders();
  const orientationPortrait = useOrientationPortrait();
  const { deleteAuthData } = useChangeAuthData();
  const mediumScreen = useMediumScreen();
  const ResponsiveContainer = useResponsiveContainer();
  const { setModalData, authData, setDisplayMenuModal, setModalizeData } = React.useContext(AppContext);
  const { currentVenue, setCurrentCommerce, isConnected, currentCommerce } = React.useContext(OrderContext);
  const [inputText, setInputText] = React.useState('');
  const [activeOrderId, setActiveOrderId] = React.useState<string | number>('');
  const [isIntervalEnabled, setIsIntervalEnabled] = React.useState(true);
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [inProcessOrders, setInProcessOrders] = React.useState<InProcessOrders>({
    pending: 0,
    inProcess: 0,
    toDelivery: 0,
  });
  const [filteredOrders, setFilteredOrders] = React.useState<Order[]>([]);
  const [totalBilling, setTotalBilling] = React.useState<number>(0);
  const [showFilterByDelivery, setShowFilterByDelivery] = React.useState<boolean>(false);
  const [showFilterByOrderStatus, setShowFilterByOrderStatus] = React.useState<boolean>(false);
  const [sortOrdersByMostRecent, setSortOrdersByMostRecent] = React.useState<boolean>(false);
  const [checkedDelivery, setCheckedDelivery] = React.useState<string>('');
  const [checkedStatus, setCheckedStatus] = React.useState<string>('');
  const [showCamera, setShowCamera] = React.useState(false);
  const [page, setPage] = React.useState<number>(1);
  const [total, setTotal] = React.useState<number>(0);
  const [scanned, setScanned] = React.useState(false);
  const refToCurrentCommerce = React.useRef<Commerce | null>(null);
  const ordersRequestId = React.useRef(0);
  refToCurrentCommerce.current = currentCommerce;

  const isCommerceEditor = authData?.user.role === UserRoleType.CommerceEditor;
  const isVenueEditor = authData?.user.role === UserRoleType.VenueEditor;
  const isFranchiseEditor = authData?.user.role === UserRoleType.FranchiseEditor;

  const openQRscanner = () => {
    setShowCamera(true);
  };

  const modalMessageContent = (
    title: string,
    message: string,
    isSuccess: boolean,
    onClose?: () => void,
  ): JSX.Element => (
    <SuccessModal
      isSuccess={isSuccess}
      title={title}
      mainColor={isSuccess ? '#00FF00' : '#FF0000'}
      message={message}
      buttonText={t('back')}
      isMediumScreen={mediumScreen}
      onConfirm={() => {
        setModalizeData(null);
        if (onClose) onClose();
      }}
    />
  );

  const openModalMessage = (title: string, message: string, success: boolean, onClose?: () => void) => {
    setModalizeData({
      content: modalMessageContent(title, message, success, onClose),
      closeOnOverlayTap: false,
    });
  };

  const reopenScanner = () => setScanned(false);

  const reimpressOrderOnScan = async (order: Order, scannedProductId?: string) => {
    const commonData = {
      order,
      isPortraitScreen: orientationPortrait,
      paymentMethod: getPaymentKey(order),
      eventName: currentVenue?.eventName,
      isFromSitefApp: Boolean(scannedProductId),
    };
    if (scannedProductId) {
      await handleUniqueProductImpression({
        ...commonData,
        productId: scannedProductId,
      });
    } else {
      await handleTicketsImpression({
        ...commonData,
        impressOneTicket: true,
      });
    }
  };

  const onQrRead = async (qrOrderNumber: number | null) => {
    if (qrOrderNumber === null || isNaN(qrOrderNumber)) {
      Alert.alert('Error', t('scan.invalid-code'));
      setShowCamera(false);
      return;
    }

    if (scanned) return;

    setScanned(true);

    try {
      if (!currentCommerce) {
        Alert.alert('Error', 'currentCommerce no encontrado');
        return;
      }

      const response = await deliverOrderByQr({
        code: qrOrderNumber,
        commerceId: currentCommerce.id,
      });

      if (!response) {
        openModalMessage(
          t('error-title'),
          t('scan.error-message', { orderNumber: qrOrderNumber }),
          false,
          reopenScanner,
        );
        return;
      }

      const { status, orderId, order: completeOrder, scannedProductId } = response;
      const orderIdTranslation = { orderNumber: orderId };
      const hasToImpressProductTicket = completeOrder && completeOrder.isPreOrder;

      switch (status) {
        case 'delivered-success':
          if (hasToImpressProductTicket) {
            try {
              await reimpressOrderOnScan(completeOrder, scannedProductId);
            } catch {}
          }
          openModalMessage(
            t('scan.delivered-success-title'),
            t('scan.delivered-success-message', orderIdTranslation),
            true,
            reopenScanner,
          );
          break;

        case 'delivered':
          openModalMessage(t('error-title'), t('scan.delivered-already', orderIdTranslation), false, reopenScanner);
          break;

        case 'partial-delivery':
          if (hasToImpressProductTicket) {
            try {
              await reimpressOrderOnScan(completeOrder, scannedProductId);
            } catch {}
          }
          openModalMessage(
            t('scan.order-updated', orderIdTranslation),
            t('scan.partial-delivery', {
              orderNumber: orderId,
              validated: response.validated,
              total: response.total,
              productName: response.productName ? `${response.productName}: ` : '',
            }),
            true,
            reopenScanner,
          );
          break;

        case 'total-product-delivered-success':
          if (hasToImpressProductTicket) {
            try {
              await reimpressOrderOnScan(completeOrder, scannedProductId);
            } catch {}
          }
          openModalMessage(
            t('scan.product-delivered-success-title', orderIdTranslation),
            t('scan.product-delivered-sucess-message', {
              orderNumber: orderId,
              validated: response.validated,
              total: response.total,
              productName: response.productName,
            }),
            true,
            reopenScanner,
          );
          break;

        case 'product-already-fully-delivered':
          openModalMessage(
            t('scan.default-title', orderIdTranslation),
            t('scan.product-already-fully-delivered-message', { productName: response.productName }),
            false,
            reopenScanner,
          );
          break;

        case 'cancelled':
          openModalMessage(t('error-title'), t('order-canceled', orderIdTranslation), false, reopenScanner);
          break;

        case 'not-belong-to-commerce':
          openModalMessage(t('error-title'), t('order-not-belong', orderIdTranslation), false, reopenScanner);
          break;

        case 'not-found':
          openModalMessage(t('error-title'), t('order-not-found', orderIdTranslation), false, reopenScanner);
          break;

        default:
          openModalMessage(t('scan-error-title'), t('scan.error-message', orderIdTranslation), false, reopenScanner);
          break;
      }
    } catch (error) {
      console.log(error);
      Alert.alert('Error', 'Ocurrió un problema al escanear el código. ' + JSON.stringify(error));
      setScanned(false);
    }
  };

  const currentDateTime = mediumScreen ? moment().format('DD/MM/YYYY hh:mm') : null;
  const isParkingCommerce = currentCommerce?.type.description === 'Parking';

  const sortOrdersByMostRecentRef = React.useRef(sortOrdersByMostRecent);

  React.useEffect(() => {
    sortOrdersByMostRecentRef.current = sortOrdersByMostRecent;
  }, [sortOrdersByMostRecent]);

  const fetchOrders = React.useCallback(
    async (orderParams?: { orderId?: string | number; orderBy?: string | null; pageOverride?: number }) => {
      if (!refToCurrentCommerce.current || !currentVenue) return;

      const commerceId = refToCurrentCommerce.current.id;
      const venueId = currentVenue.id;
      const requestPage = orderParams?.pageOverride ?? page;
      const requestedOrderId = orderParams?.orderId ?? activeOrderId;
      const params: any =
        requestedOrderId !== undefined && ![NaN, -1, ''].includes(requestedOrderId)
          ? { orderId: requestedOrderId }
          : {};

      params.billingRange = currentVenue.billingRange ?? 12;
      const requestId = ++ordersRequestId.current;

      try {
        const [ordersResult, inProcessResult] = await Promise.allSettled([
          getPaginatedOrders(
            commerceId,
            venueId,
            params,
            requestPage,
            orderParams?.orderBy ?? (sortOrdersByMostRecentRef.current ? 'desc' : 'asc'),
          ),
          getInProcessOrders(commerceId, venueId, params.billingRange),
        ]);

        // Si otra carga empezó mientras esta estaba en vuelo, ignoramos la respuesta vieja.
        if (requestId !== ordersRequestId.current) return;

        if (commerceId !== refToCurrentCommerce.current?.id || venueId !== currentVenue.id) {
          return;
        }

        if (ordersResult.status === 'fulfilled') {
          setOrders(ordersResult.value.data);
          setTotal(ordersResult.value.total);
        } else {
          console.log('Error loading orders:', ordersResult.reason);
          setOrders([]);
        }

        if (inProcessResult.status === 'fulfilled') {
          setInProcessOrders(inProcessResult.value);
        setTotalBilling(inProcessResult.value.totalAmount);
        } else {
          console.log('Error loading in-process orders:', inProcessResult.reason);
        }
      } catch (error) {
        console.log(error);
        setOrders([]);
      }
    },
    [activeOrderId, currentVenue, page],
  );

  const sortOrdersByMostRecentDateTime = async (): Promise<void> => {
    const nextSort = !sortOrdersByMostRecent;
    setSortOrdersByMostRecent(nextSort);

    await fetchOrders({ orderBy: nextSort ? 'desc' : 'asc' });
  };

  const handleSearch = async () => {
    const orderId = inputText ?? '';

    setIsIntervalEnabled(!orderId || orderId === '-1');
    setActiveOrderId(orderId);

    // La búsqueda siempre vuelve a la primera página.
    // El effect único de carga detecta el cambio de activeOrderId/page y hace el request.
    if (page !== 1) {
      setPage(1);
    }
  };

  // Único punto de carga automática de órdenes.
  // No se ejecuta al cambiar inputText: buscar es una acción explícita.
  React.useEffect(() => {
    if (!currentCommerce || !currentVenue) return;

    fetchOrders({ orderId: activeOrderId });
  }, [activeOrderId, currentCommerce, currentVenue, page, fetchOrders]);

  const findUserCommerce = React.useCallback(() => {
    if (!currentVenue) return;

    if (isCommerceEditor) {
      const userCommerce = currentVenue.commerces.reduce((value) => value);

      if (userCommerce) {
        setCurrentCommerce(userCommerce);
      }

      return;
    }

    if (!currentCommerce) {
      setTimeout(() => {
        navigation.reset({
          index: 1,
          routes: [{ name: 'VenueSelectionScreen' }, { name: 'CommercesScreen' }],
        });
      }, 500);
    }
  }, [currentCommerce, currentVenue, isCommerceEditor, navigation, setCurrentCommerce]);

  React.useEffect(() => {
    if (!isIntervalEnabled) return;

    if (!currentVenue) {
      setTimeout(() => {
        navigation.reset({
          index: 0,
          routes: [{ name: 'VenueSelectionScreen' }],
        });
      }, 500);
      return;
    }

    findUserCommerce();
  }, [currentVenue, isIntervalEnabled, findUserCommerce, navigation]);

  React.useEffect(() => {
    const uploadingPendingOrders = async () => {
      await processPendingOrders();
    };
    uploadingPendingOrders();
  }, [isConnected]);

  React.useEffect(() => {
    if (showCamera) {
      setIsIntervalEnabled(false);
    } else {
      if (!isIntervalEnabled) {
        setIsIntervalEnabled(true);
      }
    }
  }, [showCamera]);

  if (showCamera && Platform.OS !== 'web') {
    return (
      <QRScanner
        onRead={onQrRead}
        isOrientationPortrait={orientationPortrait}
        isMediumScreen={mediumScreen}
        scanned={scanned}
        setShowCamera={setShowCamera}
        isBarCode={currentVenue?.pickupVerificationMethod === 2}
      />
    );
  }

  return (
    <ResponsiveContainer style={styles.container}>
      <View style={mediumScreen ? styles.header : styles.headerSmall}>
        <Icon
          name={isCommerceEditor ? 'menu-outline' : 'arrow-back-outline'}
          type="ionicon"
          color="#001C47"
          size={40}
          containerStyle={mediumScreen ? styles.goBackContainerLarge : styles.goBackContainerSmall}
          onPress={() => {
            if (isCommerceEditor) {
              setDisplayMenuModal(true);
            } else {
              navigation.goBack();
            }
          }}
        />
        {mediumScreen ? (
          <View style={styles.upperTextContainerLarge}>
            <Text style={styles.textTitleLargeOrders}>
              <Text
                style={[styles.textTitleLargeOrders, styles.textTitleLargeCommerce]}
              >{`${currentCommerce?.name} >`}</Text>
              {t('orders')}
            </Text>
            <View style={{ marginTop: 25, marginLeft: 45 }}>
              <Text
                style={{
                  fontSize: 18,
                  fontWeight: '400',
                  lineHeight: 20,
                  textAlign: 'left',
                  color: '#001639',
                }}
              >{`${currentDateTime}hs`}</Text>
            </View>
          </View>
        ) : (
          <Text style={mediumScreen ? styles.textLarge : styles.textSmall}>{currentCommerce?.name}</Text>
        )}
        {mediumScreen ? (
          <>
            {Platform.OS !== 'web' && (
              <View
                style={{
                  flex: 0.08,
                  flexDirection: 'column',
                  alignSelf: 'flex-end',
                  left: 180,
                }}
              >
                <TouchableOpacity onPress={() => openQRscanner()} style={styles.scannerButton}>
                  <Ionicons name="scan-outline" size={30} color="white" />
                </TouchableOpacity>
              </View>
            )}
            <InputSearch
              value={inputText}
              setInput={setInputText}
              handleSearch={handleSearch}
              isMediumScreen={mediumScreen}
              placeholderText={t('search-by-order-number')}
              keyboardType="numeric"
            />
          </>
        ) : (
          <Icon
            name="person-circle-sharp"
            type="ionicon"
            color="#001639"
            size={40}
            containerStyle={mediumScreen ? styles.iconLarge : styles.iconSmall}
            onPress={() =>
              setModalData({
                description: t('logout'),
                generalColor: currentVenue?.appColor ?? '#001C47',
                action: async () => {
                  await deleteAuthData();
                },
              })
            }
          />
        )}
      </View>
      <View
        style={{
          flexWrap: mediumScreen ? 'wrap' : 'nowrap',
          flexDirection: mediumScreen ? 'row' : 'column',
          marginHorizontal: mediumScreen ? 35 : 0,
        }}
      >
        {(isVenueEditor || isFranchiseEditor) && (
          <>
            <View
              style={[
                mediumScreen ? styles.totalContainerLarge : styles.totalContainerSmall,
                { backgroundColor: currentVenue?.appColor ?? '#fff' },
              ]}
            >
              <View
                style={[
                  mediumScreen ? styles.totalTextContainerAdjustmentsLarge : styles.totalTextContainerAdjustmentsSmall,
                ]}
              >
                <Text style={mediumScreen ? styles.totalTextTitleLarge : styles.totalTextTitleSmall}>
                  {t('total-balance')}
                </Text>
                <Text style={mediumScreen ? styles.totalTextBalanceLarge : styles.totalTextBalanceSmail}>
                  {parseAmounts(totalBilling, i18n.language as SupportedLanguages)}
                </Text>
              </View>
            </View>
          </>
        )}

        <View style={mediumScreen ? styles.cardsContainerLarge : styles.cardsContainerSmall}>
          <View style={styles.cardColumn}>
            <View style={[mediumScreen ? styles.orderStatusContainerLarge : styles.orderStatusContainerSmall]}>
              <Text style={[mediumScreen ? styles.orderStatusTitleLarge : styles.orderStatusTitleSmall]}>
                {t('pendings')}
              </Text>
              <Text style={mediumScreen ? styles.orderStatusNumberLarge : styles.orderStatusNumberSmall}>
                {inProcessOrders.pending}
              </Text>
            </View>
          </View>
          <View style={styles.cardColumn}>
            <View style={[mediumScreen ? styles.orderStatusContainerLarge : styles.orderStatusContainerSmall]}>
              <Text style={[mediumScreen ? styles.orderStatusTitleLarge : styles.orderStatusTitleSmall]}>
                {t('in-proccess')}
              </Text>
              <Text style={mediumScreen ? styles.orderStatusNumberLarge : styles.orderStatusNumberSmall}>
                {inProcessOrders.inProcess}
              </Text>
            </View>
          </View>
          <View style={styles.cardColumn}>
            <View style={[mediumScreen ? styles.orderStatusContainerLarge : styles.orderStatusContainerSmall]}>
              <Text style={[mediumScreen ? styles.orderStatusTitleLarge : styles.orderStatusTitleSmall]}>
                {t('to-deliver')}
              </Text>
              <Text style={mediumScreen ? styles.orderStatusNumberLarge : styles.orderStatusNumberSmall}>
                {inProcessOrders.toDelivery}
              </Text>
            </View>
          </View>
        </View>
      </View>
      <ScrollView style={styles.scrollItemsContainer}>
        {isCommerceEditor && (
          <View
            style={mediumScreen ? styles.registerOrderButtonContainerLarge : styles.registerOrderButtonContainerSmall}
          >
            <Button
              title={t('register-order')}
              type="solid"
              containerStyle={mediumScreen ? styles.orderBtnContainerLarge : styles.orderBtnContainerSmall}
              titleStyle={styles.btnTitleOrders}
              buttonStyle={mediumScreen ? styles.orderBtnLarge : styles.orderBtnSmall}
              onPress={() => navigation.navigate('ShoppingScreen')}
              disabled={!currentCommerce}
            />
          </View>
        )}
        {currentCommerce && (
          <OrdersList
            orders={filteredOrders.length > 0 ? filteredOrders : orders}
            navigation={navigation}
            isMediumScreen={mediumScreen}
          >
            <>
              {!mediumScreen && (
                <View style={mediumScreen ? styles.header : styles.headerSmall}>
                  <InputSearch
                    value={inputText}
                    setInput={setInputText}
                    handleSearch={handleSearch}
                    isMediumScreen={mediumScreen}
                    placeholderText={t('search-by-order-number')}
                    keyboardType="numeric"
                  />
                  {Platform.OS !== 'web' && (
                    <TouchableOpacity onPress={() => openQRscanner()} style={styles.scannerButton}>
                      <Ionicons name="scan-outline" size={30} color="white" />
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </>
            <View
              style={mediumScreen ? styles.topRowFiltersOnOrdersListLarge : [styles.topRowFiltersOnOrdersListSmall]}
            >
              {mediumScreen ? (
                <>
                  <Pressable
                    style={styles.topRowOrderItemOnOrdersListLarge}
                    onPress={() => {
                      sortOrdersByMostRecentDateTime();
                    }}
                  >
                    <View>
                      <Text style={styles.topRowText}># {t('order')} ↑</Text>
                    </View>
                  </Pressable>
                  <View style={{ flex: 0.75, flexDirection: 'row', alignSelf: 'center', justifyContent: 'center' }}>
                    {!isParkingCommerce && (
                      <>
                        <Pressable
                          onPress={() => {
                            setShowFilterByDelivery(true);
                          }}
                          style={styles.topRowItemOnOrdersListLarge}
                        >
                          <View>
                            <Text style={styles.topRowTextNotOrderItem}>{t('delivery')} ↓</Text>
                          </View>
                        </Pressable>
                        <OrderDeliveryActionMenu
                          handleFilter={(delivery: string) => {
                            setShowFilterByDelivery(false);
                            if (!delivery) {
                              setFilteredOrders([]);
                              return;
                            }
                            const filteredOrders = orders.filter((order) => {
                              if (delivery === 'Delivery') {
                                return order.delivery;
                              } else {
                                return !order.delivery;
                              }
                            });

                            if (filteredOrders.length > 0) {
                              setFilteredOrders(filteredOrders);
                            }
                          }}
                          visible={showFilterByDelivery}
                          setVisible={setShowFilterByDelivery}
                          selectedCheckbox={checkedDelivery}
                          setSelectedCheckbox={setCheckedDelivery}
                          isMediumScreen={mediumScreen}
                        />
                      </>
                    )}
                    <Pressable
                      onPress={() => {
                        setShowFilterByOrderStatus(true);
                      }}
                      style={styles.topRowItemOnOrdersListLarge}
                    >
                      <View>
                        <Text style={styles.topRowTextNotOrderItem}>{t('status')} ↓</Text>
                      </View>
                    </Pressable>
                    <OrderStatusActionMenu
                      handleFilter={(status: string) => {
                        setShowFilterByOrderStatus(false);
                        if (!status) {
                          setFilteredOrders([]);
                          return;
                        }
                        const filteredOrders = orders.filter((order) => {
                          if (status === 'Pending') {
                            return (
                              order.status === OrderStatusType.CREATED_STATUS ||
                              order.status === OrderStatusType.PAYMENT_ACCEPTED_STATUS ||
                              order.status === OrderStatusType.TO_DELIVER_STATUS
                            );
                          } else if (status === 'In Process') {
                            return order.status === OrderStatusType.PREPARING_STATUS;
                          } else if (status === 'Delivering') {
                            return order.status === OrderStatusType.DELIVERING_STATUS;
                          } else {
                            return order;
                          }
                        });

                        if (filteredOrders.length > 0) {
                          setFilteredOrders(filteredOrders);
                        }
                      }}
                      visible={showFilterByOrderStatus}
                      setVisible={setShowFilterByOrderStatus}
                      selectedCheckbox={checkedStatus}
                      setSelectedCheckbox={setCheckedStatus}
                      isMediumScreen={mediumScreen}
                    />
                  </View>
                </>
              ) : (
                <>
                  <Pressable
                    style={styles.topRowOrderItemOnOrdersListSmall}
                    onPress={() => sortOrdersByMostRecentDateTime()}
                  >
                    <View>
                      <Text style={styles.topRowText}># {t('order')} ↑</Text>
                    </View>
                  </Pressable>
                  {!isParkingCommerce && (
                    <>
                      <Pressable
                        style={styles.topRowOrderItemOnOrdersListSmall}
                        onPress={() => {
                          setShowFilterByDelivery(true);
                        }}
                      >
                        <View>
                          <Text style={styles.topRowText}>{t('delivery')} ↓</Text>
                        </View>
                      </Pressable>
                      <OrderDeliveryActionMenu
                        handleFilter={(delivery: string) => {
                          setShowFilterByDelivery(false);
                          if (!delivery) {
                            setFilteredOrders([]);
                            return;
                          }
                          const filteredOrders = orders.filter((order) => {
                            if (delivery === 'Delivery') {
                              return order.delivery;
                            } else {
                              return !order.delivery;
                            }
                          });

                          if (filteredOrders.length > 0) {
                            setFilteredOrders(filteredOrders);
                          }
                        }}
                        visible={showFilterByDelivery}
                        setVisible={setShowFilterByDelivery}
                        selectedCheckbox={checkedDelivery}
                        setSelectedCheckbox={setCheckedDelivery}
                        isMediumScreen={mediumScreen}
                      />
                    </>
                  )}
                  <>
                    <Pressable
                      style={styles.topRowOrderItemOnOrdersListSmall}
                      onPress={() => {
                        setShowFilterByOrderStatus(true);
                      }}
                    >
                      <View>
                        <Text style={styles.topRowText}>{t('status')} ↓</Text>
                      </View>
                    </Pressable>
                    <OrderStatusActionMenu
                      handleFilter={(status: string) => {
                        setShowFilterByOrderStatus(false);
                        if (!status) {
                          setFilteredOrders([]);
                          return;
                        }
                        const filteredOrders = orders.filter((order) => {
                          if (status === 'Pending') {
                            return (
                              order.status === OrderStatusType.CREATED_STATUS ||
                              order.status === OrderStatusType.PAYMENT_ACCEPTED_STATUS ||
                              order.status === OrderStatusType.TO_DELIVER_STATUS
                            );
                          } else if (status === 'In Process') {
                            return order.status === OrderStatusType.PREPARING_STATUS;
                          } else if (status === 'Delivering') {
                            return order.status === OrderStatusType.DELIVERING_STATUS;
                          } else {
                            return order;
                          }
                        });

                        if (filteredOrders.length > 0) {
                          setFilteredOrders(filteredOrders);
                        }
                      }}
                      visible={showFilterByOrderStatus}
                      setVisible={setShowFilterByOrderStatus}
                      selectedCheckbox={checkedStatus}
                      setSelectedCheckbox={setCheckedStatus}
                      isMediumScreen={mediumScreen}
                    />
                  </>
                </>
              )}
            </View>
            <>
              {mediumScreen && (
                <View
                  style={[
                    styles.topRowForDescriptionOnListOrders,
                    { backgroundColor: currentVenue?.appColor ?? '#000000' },
                  ]}
                >
                  <View style={{ flex: 0.5, flexDirection: 'column', justifyContent: 'flex-start' }}>
                    <Text style={styles.topRowTextForDescription}>{t('order')}</Text>
                  </View>
                  <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'flex-start' }}>
                    <Text style={styles.topRowTextForDescription}>{t('date')}</Text>
                  </View>
                  <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'flex-start' }}>
                    <Text style={styles.topRowTextForDescription}>{isParkingCommerce ? t('car') : t('delivery')}</Text>
                  </View>
                  {isParkingCommerce && (
                    <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'flex-start' }}>
                      <Text style={styles.topRowTextForDescription}>{t('plate')}</Text>
                    </View>
                  )}
                  <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'flex-start' }}>
                    <Text style={styles.topRowTextForDescription}>{t('status')}</Text>
                  </View>
                  <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'flex-start' }}>
                    <Text style={styles.topRowTextForDescription}>{t('contact')}</Text>
                  </View>
                  {!isParkingCommerce && (
                    <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'flex-start' }}>
                      <Text style={styles.topRowTextForDescription}>Email</Text>
                    </View>
                  )}
                </View>
              )}
            </>
          </OrdersList>
        )}
      </ScrollView>
      <View style={styles.paginationContainer}>
        <View style={mediumScreen ? styles.paginationButtons : styles.paginationButtonsSmall}>
          <Button title="Anterior" onPress={() => setPage((prev) => Math.max(prev - 1, 1))} disabled={page <= 1} />

          <Text style={{ fontSize: 16 }}>
            {page} / {Math.ceil(total / 15)}
          </Text>

          <Button
            title="Siguiente"
            onPress={() => setPage((prev) => prev + 1)}
            disabled={page >= Math.ceil(total / 15)}
          />
        </View>
      </View>
    </ResponsiveContainer>
  );
};
